import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card } from '@/design-system/primitives'
import { EventTitle } from '@/features/event/components/EventTitle'
import { cn } from '@/lib/cn'
import type { TitleSegment } from '@/lib/theme'

/**
 * L'identité visuelle de l'édition : son titre composé, et ses couleurs.
 *
 * C'est l'écran qui rend une plateforme réutilisable. « THE MASKED NIGHT des
 * Suspendus » n'est pas écrit dans le code, et le suivant non plus : le nom,
 * son découpage, la fonte de chaque morceau et les couleurs du site sont des
 * **données**. Changer d'événement ne demande alors ni développeur ni
 * déploiement.
 *
 * Deux partis pris méritent d'être connus :
 *
 * * **L'aperçu est le vrai composant.** Ce n'est pas une imitation dessinée
 *   pour l'occasion mais `EventTitle` lui-même, celui de la page d'accueil.
 *   Une maquette approximative finirait par mentir, et on ne le découvrirait
 *   qu'en publiant.
 * * **La taille est un pourcentage, jamais des pixels.** La base du titre est
 *   responsive ; un segment à 145 % reste à 145 % de quelque chose qui tient
 *   sur un téléphone. Une taille en pixels choisie devant un grand écran
 *   déborderait de tous les autres.
 */
const FONTS = ['display', 'script', 'sans'] as const

const THEME_KEYS = [
  'bgBase',
  'bgElevated',
  'bgOverlay',
  'textPrimary',
  'textSecondary',
  'ember300',
  'ember400',
  'ember500',
  'champagne400',
] as const

const BLANK: TitleSegment = {
  text: '',
  font: 'display',
  scale: 100,
  fill: 'champagne',
  color: '#d9c08a',
  weight: 500,
  italic: false,
  uppercase: true,
  letter_spacing: 0,
  offset_y: 0,
  offset_x: 0,
  new_line: true,
}

export function BrandEditor({
  name,
  segments,
  theme,
  busy,
  error,
  onSave,
}: {
  name: string
  segments: TitleSegment[]
  theme: Record<string, unknown>
  busy: boolean
  error: string | null
  onSave: (payload: Record<string, unknown>) => void
}) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<TitleSegment[]>(segments)
  const [colors, setColors] = useState<Record<string, string>>(() => {
    const start: Record<string, string> = {}
    for (const key of THEME_KEYS) {
      const value = theme[key]
      if (typeof value === 'string') start[key] = value
    }
    return start
  })

  const patch = (index: number, changes: Partial<TitleSegment>) =>
    setDraft((current) =>
      current.map((row, position) => (position === index ? { ...row, ...changes } : row)),
    )

  const move = (index: number, delta: number) =>
    setDraft((current) => {
      const next = [...current]
      const target = index + delta
      if (target < 0 || target >= next.length) return current
      ;[next[index], next[target]] = [next[target]!, next[index]!]
      return next
    })

  return (
    <Card className="mt-8">
      <h2 className="font-display text-heading-md text-content-primary">
        {t('admin.brand.title')}
      </h2>
      <p className="mt-1 text-sm text-content-secondary">{t('admin.brand.hint')}</p>

      {/* L'aperçu est le composant de la page d'accueil, pas une imitation. */}
      <div className="mt-6 overflow-x-auto rounded-lg border border-line-subtle bg-bg-base px-6 py-8">
        <EventTitle name={name} segments={draft} />
      </div>

      <p className="mt-6 overline text-content-muted">{t('admin.brand.segments')}</p>
      <div className="mt-3 space-y-4">
        {draft.map((segment, index) => (
          <SegmentRow
            key={index}
            segment={segment}
            first={index === 0}
            last={index === draft.length - 1}
            onChange={(changes) => patch(index, changes)}
            onMove={(delta) => move(index, delta)}
            onRemove={() =>
              setDraft((current) => current.filter((_, position) => position !== index))
            }
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setDraft((current) => [...current, { ...BLANK }])}
        >
          {t('admin.brand.addSegment')}
        </Button>
        {draft.length > 0 ? (
          <Button variant="ghost" size="sm" onClick={() => setDraft([])}>
            {t('admin.brand.clear')}
          </Button>
        ) : null}
      </div>

      {draft.length === 0 ? (
        <p className="mt-3 text-caption text-content-muted">{t('admin.brand.emptyHint')}</p>
      ) : null}

      {/* Les couleurs du site. Neuf variables, pas davantage : ce sont celles
          que le thème sait appliquer, et en exposer d'autres promettrait un
          effet qui ne se produirait pas. */}
      <p className="mt-8 overline text-content-muted">{t('admin.brand.colors')}</p>
      <p className="mt-1 text-caption text-content-muted">{t('admin.brand.colorsHint')}</p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {THEME_KEYS.map((key) => (
          <label
            key={key}
            className="flex items-center gap-3 rounded-md border border-line-subtle px-3 py-2"
          >
            <input
              type="color"
              value={colors[key] ?? DEFAULTS[key]}
              onChange={(event) =>
                setColors((current) => ({ ...current, [key]: event.target.value }))
              }
              className="h-8 w-10 shrink-0 cursor-pointer rounded border-0 bg-transparent"
            />
            <span className="min-w-0">
              <span className="block text-sm text-content-primary">
                {t(`admin.brand.color.${key}`)}
              </span>
              <span className="block font-mono text-caption text-content-muted">
                {colors[key] ?? t('admin.brand.colorDefault')}
              </span>
            </span>
          </label>
        ))}
      </div>

      {error ? <p className="mt-4 text-sm text-ember-300">{error}</p> : null}

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          disabled={busy || draft.some((segment) => !segment.text.trim())}
          onClick={() =>
            onSave({
              title_style: draft.map((segment) => ({
                ...segment,
                text: segment.text.trim(),
              })),
              theme: { ...theme, ...colors },
            })
          }
        >
          {t('admin.project.save')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            setDraft(segments)
            setColors({})
          }}
        >
          {t('admin.brand.reset')}
        </Button>
      </div>
      <p className="mt-2 text-caption text-content-muted">{t('admin.brand.reloadHint')}</p>
    </Card>
  )
}

/** Les valeurs du thème par défaut — pour que le sélecteur parte d'une teinte. */
const DEFAULTS: Record<string, string> = {
  bgBase: '#0b0b0d',
  bgElevated: '#121316',
  bgOverlay: '#191a1e',
  textPrimary: '#f4f1ec',
  textSecondary: '#a6a29b',
  ember300: '#f2913a',
  ember400: '#e85a38',
  ember500: '#d8321f',
  champagne400: '#d9c08a',
}

function SegmentRow({
  segment,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  segment: TitleSegment
  first: boolean
  last: boolean
  onChange: (changes: Partial<TitleSegment>) => void
  onMove: (delta: number) => void
  onRemove: () => void
}) {
  const { t } = useTranslation()

  return (
    <div className="rounded-lg border border-line-subtle p-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-48 flex-1 text-caption text-content-muted">
          {t('admin.brand.text')}
          <input
            value={segment.text}
            onChange={(event) => onChange({ text: event.target.value })}
            className={INPUT}
          />
        </label>

        <label className="text-caption text-content-muted">
          {t('admin.brand.font')}
          <select
            value={segment.font}
            onChange={(event) => onChange({ font: event.target.value as TitleSegment['font'] })}
            className={cn(INPUT, 'w-36')}
          >
            {FONTS.map((font) => (
              <option key={font} value={font}>
                {t(`admin.brand.fonts.${font}`)}
              </option>
            ))}
          </select>
        </label>

        <label className="text-caption text-content-muted">
          {t('admin.brand.scale')}
          <input
            type="number"
            min={20}
            max={300}
            step={5}
            value={segment.scale}
            onChange={(event) => onChange({ scale: clamp(event.target.value, 20, 300) })}
            className={cn(INPUT, 'w-24 tabular-nums')}
          />
        </label>

        <label className="text-caption text-content-muted">
          {t('admin.brand.weight')}
          <input
            type="number"
            min={100}
            max={900}
            step={100}
            value={segment.weight}
            onChange={(event) => onChange({ weight: clamp(event.target.value, 100, 900) })}
            className={cn(INPUT, 'w-24 tabular-nums')}
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-3">
        <label className="text-caption text-content-muted">
          {t('admin.brand.fill')}
          <select
            value={segment.fill}
            onChange={(event) => onChange({ fill: event.target.value as TitleSegment['fill'] })}
            className={cn(INPUT, 'w-40')}
          >
            <option value="champagne">{t('admin.brand.fills.champagne')}</option>
            <option value="solid">{t('admin.brand.fills.solid')}</option>
          </select>
        </label>

        {segment.fill === 'solid' ? (
          <label className="text-caption text-content-muted">
            {t('admin.brand.colorLabel')}
            <input
              type="color"
              value={segment.color}
              onChange={(event) => onChange({ color: event.target.value })}
              className={cn(INPUT, 'w-20 cursor-pointer p-1')}
            />
          </label>
        ) : null}

        <label className="text-caption text-content-muted">
          {t('admin.brand.spacing')}
          <input
            type="number"
            min={-0.2}
            max={1}
            step={0.01}
            value={segment.letter_spacing}
            onChange={(event) =>
              onChange({ letter_spacing: clampFloat(event.target.value, -0.2, 1) })
            }
            className={cn(INPUT, 'w-24 tabular-nums')}
          />
        </label>

        {/* Le décalage vertical, c'est ce qui fait remonter « Suspendus » sur
            la ligne du dessus, comme sur l'affiche. */}
        <label className="text-caption text-content-muted">
          {t('admin.brand.offsetY')}
          <input
            type="number"
            min={-1.5}
            max={1.5}
            step={0.02}
            value={segment.offset_y}
            onChange={(event) => onChange({ offset_y: clampFloat(event.target.value, -1.5, 1.5) })}
            className={cn(INPUT, 'w-24 tabular-nums')}
          />
        </label>

        <label className="text-caption text-content-muted">
          {t('admin.brand.offsetX')}
          <input
            type="number"
            min={-2}
            max={2}
            step={0.05}
            value={segment.offset_x}
            onChange={(event) => onChange({ offset_x: clampFloat(event.target.value, -2, 2) })}
            className={cn(INPUT, 'w-24 tabular-nums')}
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4">
        <Toggle
          label={t('admin.brand.uppercase')}
          value={segment.uppercase}
          onChange={(uppercase) => onChange({ uppercase })}
        />
        <Toggle
          label={t('admin.brand.italic')}
          value={segment.italic}
          onChange={(italic) => onChange({ italic })}
        />
        <Toggle
          label={t('admin.brand.newLine')}
          value={segment.new_line}
          onChange={(new_line) => onChange({ new_line })}
        />

        <span className="ml-auto flex gap-1">
          <Button variant="ghost" size="sm" disabled={first} onClick={() => onMove(-1)}>
            ↑
          </Button>
          <Button variant="ghost" size="sm" disabled={last} onClick={() => onMove(1)}>
            ↓
          </Button>
          <Button variant="ghost" size="sm" onClick={onRemove}>
            {t('admin.brand.removeSegment')}
          </Button>
        </span>
      </div>
    </div>
  )
}

function Toggle({
  label,
  value,
  onChange,
}: {
  label: string
  value: boolean
  onChange: (value: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 text-caption text-content-secondary">
      <input
        type="checkbox"
        checked={value}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-champagne-400"
      />
      {label}
    </label>
  )
}

const INPUT =
  'mt-1 h-10 w-full rounded-md border border-line bg-bg-elevated px-3 text-sm text-content-primary'

function clamp(raw: string, min: number, max: number): number {
  const value = Number.parseInt(raw, 10)
  if (!Number.isFinite(value)) return min
  return Math.min(Math.max(value, min), max)
}

function clampFloat(raw: string, min: number, max: number): number {
  const value = Number.parseFloat(raw.replace(',', '.'))
  if (!Number.isFinite(value)) return 0
  return Math.min(Math.max(value, min), max)
}
